// BL-241 Gate 2 — left pane of the Communications redesign: one row per intake session for the
// org, newest activity first, with a client-side search box above the list.
//
// BL-241 Gate 5 wires the reserved "+ New message" slot to the compose-new contact search.
// BL-241 Gate 3 fills the upper-right corner of each row that Gate 2 reserved with the
// hover-revealed flag/status icon (CommunicationsThreadFlagControl).
// BL-177 Gate 17 — per-user message-preview lines (Off / 1 / 2 / 3, Outlook-style, default 1) in a
// hover view menu, row timestamp at the bottom-right, and hollow filter pills (green when selected).
// BL-177 Gate 17b — the preview-lines choice moved into the Communications settings modal (the
// list's own gear is gone); this list just receives `previewLines`. Tabs and channel filters are
// hollow pills with an inline colour guard so nothing upstream can paint them filled.
// BL-177 Gate 17c — a Closed conversation's row goes quiet: only the Closed tag and the Closed
// status circle remain, and every alert that still shows (channel tag, engagement badge) is grey.
// BL-177 Step 2d (board 06) — one row = avatar, name or FORMATTED number, preview, dim time, then chips: the channel
// (icon + word, no colour), the flag (colour system, never the org's custom flag colours) and the After hours /
// Group text / Short code / Scheduled tags. Short codes show the number as-is and "can't reply".

import './appChat.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { groupThreadsForList, NOT_IN_CRM_LABEL } from '../../lib/threadGrouping';
import usePullToRefresh from '../../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../ui/PullToRefreshIndicator';
import {
    rankThreadsBySearch, SEARCH_CONTAINS_MIN_CHARS, searchableThreadWords, searchNeedles, threadMatchesSearch, threadSearchRank,
} from '../../lib/communicationsThread';
import { isConversationClosed } from '../../lib/communicationsFlags';
import {
    formatThreadActivityTimestamp, threadAvatarLabel, threadDisplayName, threadFlag, threadTags,
} from '../../lib/communicationsDisplay';
import { formatPhone } from '../../lib/phone';
import ScheduledList from './ScheduledList';
import StatusChip, { ChannelChip } from '../ui/StatusChip';
import Button3D from '../ui/Button3D';
import ActionPillMenu from '../ui/ActionPillMenu';
import CommunicationsThreadFlagControl from './CommunicationsThreadFlagControl';
import { formatRelativeAge } from '../../lib/communicationsPresence';
import {
    DEFAULT_COMMUNICATIONS_SETTINGS,
    DEFAULT_PREVIEW_LINES,
    normalizeStatusCategoryOrder,
    STATUS_CATEGORY_LABELS,
} from '../../lib/communicationsSettings';
import HoverHelp from '../shared/HoverHelp';
import { mergeThreadMessages } from '../../lib/communicationsThread';
import { voicemailStateKey } from '../../lib/voicemailState';

export { NOT_IN_CRM_LABEL };

function relativeTimestamp(activityAt) {
    if (!activityAt) return '—';
    const date = new Date(activityAt);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
}

// BL-177 Step 2d: filter/tab pills use the shared 3D pressable standard (teal when selected). The name stays for the
// callers and tests that already import it; the second argument is kept for compatibility and ignored.
// eslint-disable-next-line no-unused-vars
export function hollowPillProps(isSelected, _dark = false) {
    return {
        className: `ia-btn3d ia-comms-pill ${isSelected ? 'ia-btn3d--go ia-comms-pill--on' : ''}`,
        'data-selected': isSelected ? 'true' : 'false',
    };
}

function highlightSearchText(value, term) {
    const text = String(value || '');
    const needles = searchNeedles(term);
    if (!text || needles.length === 0) return text;
    const containsNeedles = needles.filter((candidate) => candidate.length >= SEARCH_CONTAINS_MIN_CHARS);
    const parts = [];
    const matcher = /[\p{L}\p{N}@.+_-]+/gu;
    let cursor = 0;
    let match;
    while ((match = matcher.exec(text))) {
        if (match.index > cursor) parts.push(text.slice(cursor, match.index));
        const word = match[0];
        const normalized = word.toLocaleLowerCase();
        const prefix = needles.find((candidate) => normalized.startsWith(candidate));
        const inner = prefix ? null : containsNeedles.find((candidate) => normalized.includes(candidate));
        if (prefix) {
            parts.push(<mark key={`${match.index}-match`} className="rounded-sm bg-[#FDE68A] px-0.5 text-[#172033]">{word.slice(0, prefix.length)}</mark>);
            parts.push(word.slice(prefix.length));
        } else if (inner) {
            // Middle-of-word ("contains") match: highlight just the matched run.
            const at = normalized.indexOf(inner);
            parts.push(word.slice(0, at));
            // No side padding mid-word, so "airport" still reads as one word.
            parts.push(<mark key={`${match.index}-match`} className="rounded-sm bg-[#FDE68A] text-[#172033]">{word.slice(at, at + inner.length)}</mark>);
            parts.push(word.slice(at + inner.length));
        } else {
            parts.push(word);
        }
        cursor = matcher.lastIndex;
    }
    if (cursor < text.length) parts.push(text.slice(cursor));
    return parts.length ? parts : text;
}

function contactAddresses(contact, field) {
    const values = Array.isArray(contact?.[field]) ? contact[field] : [contact?.[field]];
    return values.map((value) => (
        typeof value === 'string'
            ? value
            : value?.email || value?.phone || value?.number || value?.address || value?.value
    )).filter((value) => typeof value === 'string' && value.trim());
}

/** Stable DOM id for a suggestion option, from its kind + record id (not its position, which shifts as you type). */
export function searchSuggestionOptionId(suggestion) {
    const key = `${suggestion?.kind || 'item'}-${suggestion?.id ?? suggestion?.name ?? ''}`;
    return `communications-search-suggestion-${key.replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

function matchesThreadChannel(thread, channelFilter) {
    if (channelFilter === 'all') return true;
    if (channelFilter === 'sms') return thread.channel === 'sms' || thread.channel === 'mms';
    return thread.channel === channelFilter;
}

function CommunicationsThreadList({
    threads = [],
    selectedId = null,
    // Staff Messaging Phase 1: conversations where I was @mentioned in a note and have not opened them since ("@" marker).
    mentionedSessionIds = null,
    onSelect,
    search = '',
    onSearchChange,
    otherInboxThreads = [],
    suggestionContacts = [],
    suggestionAccounts = [],
    loading = false,
    error = null,
    campaigns = [],
    campaignError = '',
    onSetFlag,
    onNewMessage,
    // Comms next pass (5b): opens a Not-in-CRM thread with its contact form open, prefilled with the number / email.
    onAddToCrm,
    onOpenCampaignThread,
    // BL-177 Step 2l: which inbox the list shows (Company inbox / My inbox), their counts, and the colleagues' conversations section.
    inboxScope = 'company',
    onInboxScopeChange,
    inboxCounts = null,
    colleagueSection = null,
    groupThreadsByAccount = false,
    groupThreadsByStatus = false,
    statusCategoryOrder = DEFAULT_COMMUNICATIONS_SETTINGS.statusCategoryOrder,
    pinnedThreadPosition = DEFAULT_COMMUNICATIONS_SETTINGS.pinnedThreadPosition,
    theme = 'light',
    userId,
    handoffNudgeMinutes = 120,
    onToggleArchive,
    previewLines = DEFAULT_PREVIEW_LINES,
    workspaceTab,
    onWorkspaceTabChange,
    teamsEnabled = false,
    // BL-177 Step 3: the Scheduled view (staff: their own; admins: the company's).
    scheduledRows = [],
    scheduledError = '',
    staffNames = {},
    isAdmin = false,
    onOpenScheduled,
    onCancelScheduled,
    quietHours = null,
    onSaveQuietHours,
    voicemailStateByKey = {},
    onUpdateVoicemailState,
    voicemailStateError = '',
    voicemailStateLoading = false,
    // Comms final pass: pull to refresh on touch devices reloads the list in place (CommunicationsView's own reload).
    onPullRefresh = null,
}) {
    const [channelFilter, setChannelFilter] = useState('all');
    const searchInputRef = useRef(null);
    const searchTimerRef = useRef(null);
    const lastSearchPropRef = useRef(search);
    const [searchDraft, setSearchDraft] = useState(search);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
    const [selectedSearchSuggestion, setSelectedSearchSuggestion] = useState(null);
    const [localInboxTab, setLocalInboxTab] = useState('conversations');
    const inboxTab = workspaceTab || localInboxTab;
    const setInboxTab = onWorkspaceTabChange || setLocalInboxTab;
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [voicemailTab, setVoicemailTab] = useState('new');
    useEffect(() => {
        if (lastSearchPropRef.current !== search) {
            lastSearchPropRef.current = search;
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
            setSearchDraft(search);
            setSelectedSearchSuggestion(null);
            setSuggestionsOpen(false);
            setActiveSuggestionIndex(-1);
        }
    }, [search]);
    useEffect(() => () => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    }, []);
    const changeSearchDraft = (value) => {
        setSearchDraft(value);
        setSuggestionsOpen(Boolean(value.trim()));
        setActiveSuggestionIndex(-1);
        setSelectedSearchSuggestion(null);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            lastSearchPropRef.current = value;
            onSearchChange?.(value);
            searchTimerRef.current = null;
        }, 150);
    };
    const suggestionCandidates = useMemo(() => [
        ...suggestionContacts.filter((contact) => contact?.name).map((contact) => {
            const searchEmails = contactAddresses(contact, 'emails');
            const searchPhones = contactAddresses(contact, 'phones');
            return {
                kind: 'contact',
                id: contact.id,
                name: contact.name,
                contact,
                searchNames: [contact.name],
                searchEmails,
                searchPhones,
                searchWords: searchableThreadWords([contact.name, ...searchEmails, ...searchPhones]),
                searchPhoneDigits: searchPhones.map((phone) => phone.replace(/\D/g, '')).filter(Boolean),
            };
        }),
        ...suggestionAccounts.filter((account) => account?.account_name).map((account) => ({
            kind: 'account',
            id: account.id,
            name: account.account_name,
            account,
            accountName: account.account_name,
            searchWords: searchableThreadWords([account.account_name]),
        })),
    ], [suggestionContacts, suggestionAccounts]);
    const suggestionThreadIndexes = useMemo(() => {
        const byContact = new Map();
        const byAccount = new Map();
        [...threads, ...otherInboxThreads].forEach((thread) => {
            (thread.searchContactIds || []).forEach((contactId) => {
                if (!byContact.has(contactId)) byContact.set(contactId, []);
                byContact.get(contactId).push(thread);
            });
            if (thread.accountId) {
                if (!byAccount.has(thread.accountId)) byAccount.set(thread.accountId, []);
                byAccount.get(thread.accountId).push(thread);
            }
        });
        return { byContact, byAccount };
    }, [threads, otherInboxThreads]);
    const searchSuggestions = useMemo(() => {
        const query = searchDraft.trim();
        if (!query) return [];
        // Prefix matches first (the clean typeahead), then middle-of-word matches; alphabetical within each.
        const matches = suggestionCandidates
            .map((candidate) => ({ candidate, rank: threadSearchRank(candidate, query) }))
            .filter(({ rank }) => rank > 0)
            .sort((a, b) => (b.rank - a.rank) || a.candidate.name.localeCompare(b.candidate.name, undefined, { sensitivity: 'base' }))
            .slice(0, 5)
            .map(({ candidate }) => candidate);
        return matches.map((candidate) => ({
            ...candidate,
            threadIds: (candidate.kind === 'contact'
                ? suggestionThreadIndexes.byContact.get(candidate.id) || (
                    candidate.contact.primary_account_id
                        ? (suggestionThreadIndexes.byAccount.get(candidate.contact.primary_account_id) || []).filter(
                            (thread) => thread.searchNames?.includes(candidate.name)
                                && thread.accountId
                                && thread.accountId === candidate.contact.primary_account_id,
                        )
                        : []
                )
                : candidate.id ? suggestionThreadIndexes.byAccount.get(candidate.id) || [] : []
            ).map((thread) => thread.id),
        }));
    }, [searchDraft, suggestionCandidates, suggestionThreadIndexes]);
    const selectSearchSuggestion = (suggestion) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
        setSuggestionsOpen(false);
        setActiveSuggestionIndex(-1);
        setSearchDraft(suggestion.name);
        lastSearchPropRef.current = suggestion.name;
        if (suggestion.kind === 'contact' && suggestion.threadIds.length === 0) {
            setSelectedSearchSuggestion(null);
            onNewMessage?.({ initialSearch: suggestion.name });
            return;
        }
        setSelectedSearchSuggestion({ kind: suggestion.kind, id: suggestion.id });
        onSearchChange?.(suggestion.name);
    };
    const handleSearchKeyDown = (event) => {
        if (event.key === 'Escape') {
            setSuggestionsOpen(false);
            setActiveSuggestionIndex(-1);
            return;
        }
        if (!suggestionsOpen || searchSuggestions.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveSuggestionIndex((index) => Math.min(index + 1, searchSuggestions.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveSuggestionIndex((index) => Math.max(index - 1, 0));
        } else if (event.key === 'Enter' && activeSuggestionIndex >= 0) {
            event.preventDefault();
            selectSearchSuggestion(searchSuggestions[activeSuggestionIndex]);
        }
    };
    const listScrollRef = useRef(null);
    const pullState = usePullToRefresh(listScrollRef, { onRefresh: onPullRefresh, enabled: typeof onPullRefresh === 'function' });
    const scheduledCount = scheduledRows.filter((row) => row.status === 'scheduled').length;
    const viewChoices = [
        ['conversations', 'Conversations'],
        ['campaigns', 'Campaigns'],
        ['scheduled', `Scheduled${scheduledCount ? ` (${scheduledCount})` : ''}`],
        ['archived', 'Archived'],
        ['voicemail', 'Voicemail'],
        ['email', 'Email inbox'],
        ...(teamsEnabled ? [['teams', 'Team chat']] : []),
    ];
    const channelChoices = [
        ['all', 'All'], ['sms', 'SMS'], ['whatsapp', 'WhatsApp'], ['app_chat', 'App chat'],
        ['voice', 'Voice'], ['email', 'Email'],
        ...(teamsEnabled ? [['teams', 'Teams']] : []),
    ];
    const currentChannel = inboxTab === 'teams-client' ? 'teams' : channelFilter;
    const currentViewLabel = viewChoices.find(([value]) => value === inboxTab)?.[1] || 'Teams';
    // BL-177 Step 2d: the list is a navy Step 2d surface in every app theme — white text, only timestamps dim.
    const labelCls = 'text-white';
    const inputCls = 'w-full min-w-0 rounded-lg border px-3 py-2 text-sm bg-[#0A1120] border-[#3B4D73] text-white placeholder-[#A9B7D0]';
    const dividerCls = 'border-[#26365A]';
    const rowBase = 'w-full text-left px-3 pt-3 pb-8 pr-12 min-h-[5.75rem] border-b transition-colors border-[#26365A] hover:bg-[#1B2A47]';

    const previewStyle = previewLines > 1
        ? { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: previewLines, overflow: 'hidden' }
        : undefined;

    // Hybrid search: cheap filters first, then prefix matches ranked above middle-of-word matches (stable order within).
    const visible = useMemo(() => rankThreadsBySearch(threads, search, (thread) => {
        if (inboxTab === 'conversations' && thread.archivedAt) return false;
        if (inboxTab === 'archived' && !thread.archivedAt) return false;
        if (selectedSearchSuggestion?.kind === 'contact'
            && !thread.searchContactIds?.includes(selectedSearchSuggestion.id)) return false;
        if (selectedSearchSuggestion?.kind === 'account' && thread.accountId !== selectedSearchSuggestion.id) return false;
        return matchesThreadChannel(thread, channelFilter);
    }), [threads, inboxTab, search, selectedSearchSuggestion, channelFilter]);
    const hiddenSearchHints = useMemo(() => {
        const hints = [];
        if (search.trim() && ['conversations', 'archived'].includes(inboxTab)) {
            const hiddenInCurrentView = threads.filter((thread) => (
                (inboxTab === 'conversations' ? Boolean(thread.archivedAt) : !thread.archivedAt)
                && matchesThreadChannel(thread, channelFilter)
                && threadMatchesSearch(thread, search)
            )).length;
            if (hiddenInCurrentView) {
                const destination = inboxTab === 'conversations' ? 'Archived' : 'Conversations';
                hints.push(`${hiddenInCurrentView} more match${hiddenInCurrentView === 1 ? '' : 'es'} in ${destination}`);
            }
            const hiddenInOtherInbox = otherInboxThreads.filter((thread) => (
                (inboxTab === 'conversations' ? !thread.archivedAt : Boolean(thread.archivedAt))
                && matchesThreadChannel(thread, channelFilter)
                && threadMatchesSearch(thread, search)
            )).length;
            if (hiddenInOtherInbox) {
                const destination = inboxScope === 'mine' ? 'Company inbox' : 'My inbox';
                hints.push(`${hiddenInOtherInbox} more match${hiddenInOtherInbox === 1 ? '' : 'es'} in ${destination}`);
            }
        }
        return hints;
    }, [search, inboxTab, threads, otherInboxThreads, inboxScope, channelFilter]);
    const allVoicemailRows = useMemo(() => threads.flatMap((thread) => mergeThreadMessages({
        conversationLog: thread.session?.conversation_log,
        rawMessageLog: thread.session?.raw_message_log,
    })
        .filter((message) => message.callStatus === 'voicemail' && message.callSid)
        .map((message) => ({
            thread,
            message,
            state: voicemailStateByKey[voicemailStateKey(thread.id, message.callSid)] || null,
        })))
        .filter(({ state }) => !state?.archived_at && !state?.deleted_at),
    [threads, voicemailStateByKey]);
    const voicemailRows = useMemo(() => allVoicemailRows.filter(({ thread, message }) => (
        !search || threadMatchesSearch(thread, search)
            || String(message.text || '').toLowerCase().includes(search.toLowerCase())
    )), [allVoicemailRows, search]);
    const visibleVoicemails = useMemo(() => voicemailRows.filter(({ state }) => inboxTab !== 'voicemail'
        || (voicemailTab === 'heard' ? Boolean(state?.heard_at) : !state?.heard_at)), [voicemailRows, inboxTab, voicemailTab]);
    const isPinnedThread = (thread) => Boolean(
        thread?.pinned
        || thread?.isPinned
        || thread?.session?.pinned
        || thread?.session?.is_pinned
        || thread?.session?.pinned_at
    );
    const arrangePinnedThreads = (rows = []) => {
        const pinned = rows.filter(isPinnedThread);
        const unpinned = rows.filter((thread) => !isPinnedThread(thread));
        return pinnedThreadPosition === 'bottom' ? [...unpinned, ...pinned] : [...pinned, ...unpinned];
    };
    // Comms next pass (5b) / final pass: "Not in CRM" only for numbers and emails nobody in the CRM has; a matched contact with
    // no account groups under "No company"; an account named by a phone number shows its person (src/lib/threadGrouping.js).
    const groupedThreads = useMemo(() => groupThreadsForList(visible), [visible]);
    const statusRank = useMemo(() => new Map(normalizeStatusCategoryOrder(statusCategoryOrder)
        .map((status, index) => [status, index])), [statusCategoryOrder]);
    const groupedStatusThreads = useMemo(() => visible.reduce((groups, thread) => {
        const status = String(thread.status || 'pending').trim().toLowerCase() || 'pending';
        const existing = groups.find((group) => group.status === status);
        if (existing) {
            existing.threads.push(thread);
        } else {
            groups.push({
                status,
                label: STATUS_CATEGORY_LABELS[status] || status,
                threads: [thread],
            });
        }
        return groups;
    }, []).sort((a, b) => {
        const aRank = statusRank.has(a.status) ? statusRank.get(a.status) : Number.MAX_SAFE_INTEGER;
        const bRank = statusRank.has(b.status) ? statusRank.get(b.status) : Number.MAX_SAFE_INTEGER;
        if (aRank !== bRank) return aRank - bRank;
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true });
    }), [visible, statusRank]);

    const addToCrmLabel = (thread) => {
        const address = thread.session?.contact_phone || thread.session?.contact_email || thread.address || '';
        return formatPhone(address) || threadDisplayName(thread);
    };
    const renderAddToCrm = (groupThreads) => {
        const addCls = 'ia-comms-add-crm inline-flex min-h-8 shrink-0 items-center rounded-full border border-[#67E8F9] px-3 text-[11px] font-semibold normal-case tracking-normal text-[#67E8F9] hover:bg-[#67E8F9]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#67E8F9]';
        if (groupThreads.length === 1) {
            const [only] = groupThreads;
            return (
                <button
                    type="button"
                    className={addCls}
                    onClick={() => onAddToCrm(only)}
                    aria-label={`Add ${addToCrmLabel(only)} to CRM`}
                    data-add-to-crm="true"
                >
                    Add to CRM
                </button>
            );
        }
        // Several people are not in the CRM: pick which one to add.
        return (
            <ActionPillMenu
                menuLabel="Add to CRM"
                triggerClassName={addCls}
                triggerProps={{ 'data-add-to-crm': 'true' }}
                placement="bottom"
                align="end"
                options={groupThreads.map((thread) => ({
                    key: thread.id,
                    label: addToCrmLabel(thread),
                    title: `Add ${addToCrmLabel(thread)} to CRM`,
                    onSelect: () => onAddToCrm(thread),
                }))}
            >
                Add to CRM
            </ActionPillMenu>
        );
    };

    const renderThread = (thread) => {
        const selected = thread.id === selectedId;
        const fullTimestamp = relativeTimestamp(thread.activityAt);
        const shortTimestamp = formatThreadActivityTimestamp(thread.activityAt) || '—';
        const closedQuiet = isConversationClosed(thread.session || { archived_at: thread.archivedAt, approval_status: thread.status });
        const flag = threadFlag(thread.session || {}) || (closedQuiet ? { key: 'closed', tone: 'grey', label: 'Closed' } : null);
        const tags = closedQuiet ? [] : threadTags(thread);
        const displayName = threadDisplayName(thread);
        const mentioned = Boolean(mentionedSessionIds?.has?.(String(thread.id).toLowerCase()));
        const handoffWaiting = !closedQuiet
            && thread.statusDisplay?.key === 'handoff'
            && thread.customerLastMessageAt !== null
            && thread.customerLastMessageAt !== undefined
            && Date.now() - thread.customerLastMessageAt > handoffNudgeMinutes * 60 * 1000;
        return (
            // The flag control sits beside the row button rather than inside it —
            // a button nested in a button is invalid markup and swallows clicks.
            <div key={thread.id} className="relative group" data-thread-row={thread.id}>
                <button
                    type="button"
                    aria-current={selected ? 'true' : undefined}
                    aria-label={`Open conversation with ${displayName}${mentioned ? ' (you were mentioned)' : ''}`}
                    onClick={() => onSelect?.(thread.id)}
                    className={`ia-hover-pulse-off relative ${rowBase} ${selected ? 'bg-[#1B2A47]' : ''}`}
                    data-closed-quiet={closedQuiet ? 'true' : undefined}
                >
                    {selected && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#14B8A6]" aria-hidden="true" />}
                    <div className="flex min-w-0 items-start gap-3">
                        <span
                            aria-hidden="true"
                            data-thread-avatar="true"
                            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold bg-[#22304F] text-white"
                        >
                            {threadAvatarLabel(thread)}
                        </span>
                        <div className="min-w-0 flex-1">
                            {mentioned ? (
                                <span className="flex min-w-0 items-center gap-1.5">
                                    <span data-thread-name="true" className="block min-w-0 truncate text-[15px] font-bold text-white">
                                        {highlightSearchText(displayName, search)}
                                    </span>
                                    <span data-thread-mention="true" className="ia-mention-marker" title="You were mentioned in a note" aria-hidden="true">@</span>
                                </span>
                            ) : (
                                <span data-thread-name="true" className="block min-w-0 truncate text-[15px] font-bold text-white">
                                    {highlightSearchText(displayName, search)}
                                </span>
                            )}
                            {previewLines > 0 && (
                                <p
                                    data-preview-lines={previewLines}
                                    className={`${previewLines === 1 ? 'truncate' : 'whitespace-pre-line break-words'} text-[13px] mt-0.5 text-white`}
                                    style={previewStyle}
                                >
                                    {highlightSearchText(thread.preview || 'No messages yet.', search)}
                                </p>
                            )}
                            {/* Chips never break inside themselves and get the full text width (the flag control sits under
                                the avatar), so channel + flag + After hours stay on one line. */}
                            <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1" data-thread-chips="true">
                                {thread.channel && <ChannelChip channel={thread.channel === 'mms' ? 'sms' : thread.channel} />}
                                {flag && flag.key !== 'none' && (
                                    <StatusChip tone={flag.tone} className="shrink-0" title={flag.label}>
                                        <span data-thread-flag={flag.key}>{flag.label}</span>
                                    </StatusChip>
                                )}
                                {!closedQuiet && thread.statusDisplay?.key === 'handoff' && (
                                    // The AI handed this conversation to staff: orange = check this.
                                    <StatusChip tone="orange"><span data-thread-tag="handoff">Handoff</span></StatusChip>
                                )}
                                {tags.map((tag) => (
                                    <StatusChip key={tag.key} tone={tag.tone}>
                                        <span data-thread-tag={tag.key}>{tag.label}</span>
                                    </StatusChip>
                                ))}
                            </div>
                            {handoffWaiting && (
                                <p className="mt-1 text-[11px] font-medium text-white">
                                    Waiting {formatRelativeAge(thread.customerLastMessageAt)}
                                </p>
                            )}
                        </div>
                    </div>
                    <span
                        data-thread-timestamp="true"
                        className="ia-d2-dim absolute bottom-2 right-3 z-10 whitespace-nowrap text-xs leading-[22px] text-[#A9B7D0]"
                        title={fullTimestamp}
                        aria-label={fullTimestamp}
                    >
                        {shortTimestamp}
                    </span>
                </button>
                <CommunicationsThreadFlagControl
                    session={thread.session}
                    onSetFlag={onSetFlag ? (change) => onSetFlag(thread.id, change) : undefined}
                    theme={theme}
                />
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full min-h-0">
            <div className={`p-3 border-b rounded-t-xl space-y-2 ${dividerCls}`} data-thread-list-header="true">
                <div className="flex items-center">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        <Button3D
                            variant="primary"
                            onClick={onNewMessage}
                            className="w-full flex-1"
                        >
                            + New message
                        </Button3D>
                    </div>
                    <HoverHelp helpId="communications.new-message" userId={userId} />
                </div>
                <div className="communications-filter-row flex flex-wrap items-center gap-1" role="group" aria-label="Thread filters">
                    {inboxCounts && ['conversations', 'archived'].includes(inboxTab) && (
                        <ActionPillMenu
                            menuLabel="Which inbox"
                            placement="right"
                            mobilePlacement="bottom"
                            scrollable
                            menuClassName="communications-filter-menu"
                            triggerClassName={hollowPillProps(true).className}
                            wrapperProps={{ 'data-inbox-scope-switch': 'true', 'data-thread-filter-group': 'inbox' }}
                            options={[
                                ['company', 'Company inbox'], ['mine', 'My inbox'],
                            ].map(([value, label]) => ({
                                key: value, label: `${label} (${inboxCounts[value] ?? 0})`,
                                active: inboxScope === value,
                                onSelect: () => onInboxScopeChange?.(value),
                            }))}
                        >
                            Inbox: {inboxScope === 'mine' ? 'My' : 'Company'} ({inboxCounts[inboxScope] ?? 0})
                        </ActionPillMenu>
                    )}
                    <ActionPillMenu
                        menuLabel="Inbox view"
                        placement="right"
                        mobilePlacement="bottom"
                        scrollable
                        menuClassName="communications-filter-menu"
                        wrapperProps={{ 'data-thread-filter-group': 'view' }}
                        triggerClassName={hollowPillProps(true).className}
                        options={viewChoices.map(([value, label]) => ({
                            key: value, label, active: inboxTab === value,
                            onSelect: () => {
                                setInboxTab(value);
                                setSelectedCampaignId('');
                            },
                        }))}
                    >
                        View: {currentViewLabel}
                    </ActionPillMenu>
                    {!['campaigns', 'scheduled', 'voicemail'].includes(inboxTab) && (
                        <ActionPillMenu
                            menuLabel="Conversation channel"
                            placement="right"
                            mobilePlacement="bottom"
                            scrollable
                            menuClassName="communications-filter-menu"
                            triggerClassName={hollowPillProps(true).className}
                            wrapperProps={{ 'data-thread-filter-group': 'channel' }}
                            options={channelChoices.map(([value, label]) => ({
                                key: value, label, active: currentChannel === value,
                                className: value === 'app_chat' ? 'm9-app-chat-filter' : '',
                                onSelect: () => {
                                    if (value === 'teams') setInboxTab('teams-client');
                                    else {
                                        setChannelFilter(value);
                                        if (inboxTab === 'teams-client') setInboxTab('conversations');
                                    }
                                },
                            }))}
                        >
                            Channel: {channelChoices.find(([value]) => value === currentChannel)?.[1]}
                        </ActionPillMenu>
                    )}
                    <span className="shrink-0" data-thread-filter-help="true">
                        <HoverHelp helpId="communications.thread-filters" userId={userId} />
                    </span>
                </div>
                {!['campaigns', 'scheduled'].includes(inboxTab) && <div className="flex items-center gap-1">
                    <div className="relative min-w-0 flex-1">
                        <input
                            ref={searchInputRef}
                            type="text"
                            role="combobox"
                            inputMode="search"
                            value={searchDraft}
                            onChange={(event) => changeSearchDraft(event.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Search conversations…"
                            aria-label="Search conversations"
                            aria-autocomplete="list"
                            aria-controls={suggestionsOpen && searchSuggestions.length > 0
                                ? 'communications-search-suggestions'
                                : undefined}
                            aria-expanded={suggestionsOpen && searchSuggestions.length > 0}
                            aria-activedescendant={suggestionsOpen && searchSuggestions[activeSuggestionIndex]
                                ? searchSuggestionOptionId(searchSuggestions[activeSuggestionIndex])
                                : undefined}
                            className={`${inputCls} min-w-0 flex-1 pr-12`}
                        />
                        {searchDraft && (
                            <button
                                type="button"
                                aria-label="Clear search"
                                onClick={() => {
                                    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                                    searchTimerRef.current = null;
                                    setSearchDraft('');
                                    setSelectedSearchSuggestion(null);
                                    setSuggestionsOpen(false);
                                    setActiveSuggestionIndex(-1);
                                    lastSearchPropRef.current = '';
                                    onSearchChange?.('');
                                    searchInputRef.current?.focus();
                                }}
                                data-clear-search="true"
                                className="absolute right-1 top-1/2 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full text-lg font-semibold text-white hover:bg-[#34496F] active:bg-[#415A85] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#03B7C9]"
                            >
                                ×
                            </button>
                        )}
                        {suggestionsOpen && searchSuggestions.length > 0 && (
                            <ul
                                id="communications-search-suggestions"
                                role="listbox"
                                aria-label="Search suggestions"
                                className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-lg border border-[#3B4D73] bg-[#111B30] p-1 shadow-xl"
                            >
                                {searchSuggestions.map((suggestion, index) => {
                                    const startMessage = suggestion.kind === 'contact' && suggestion.threadIds.length === 0;
                                    const label = startMessage
                                        ? `Start a message to ${suggestion.name}`
                                        : suggestion.name;
                                    return (
                                        // Options are not tabbable: focus stays in the combobox and aria-activedescendant
                                        // points at the active one (WAI-ARIA combobox pattern).
                                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                                        <li
                                            key={`${suggestion.kind}:${suggestion.id}`}
                                            id={searchSuggestionOptionId(suggestion)}
                                            role="option"
                                            aria-selected={activeSuggestionIndex === index}
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => selectSearchSuggestion(suggestion)}
                                            className={`block min-h-11 w-full cursor-pointer list-none rounded-md px-3 py-2 text-left text-sm text-white ${
                                                activeSuggestionIndex === index ? 'bg-[#26365A]' : 'hover:bg-[#1B2A47]'
                                            }`}
                                            data-search-suggestion-option="true"
                                        >
                                            <span className="block truncate font-semibold">
                                                {highlightSearchText(label, searchDraft)}
                                            </span>
                                            <span className="block truncate text-xs text-[#A9B7D0]">
                                                {startMessage ? 'No conversation yet' : suggestion.kind === 'account' ? 'Company account' : 'Contact'}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    <HoverHelp helpId="communications.thread-search" userId={userId} />
                </div>}
                {hiddenSearchHints.length > 0 && (
                    <p className="text-xs text-[#A9B7D0]" role="status" aria-live="polite" data-hidden-search-matches="true">
                        {hiddenSearchHints.join(' · ')}
                    </p>
                )}
            </div>
            <div ref={listScrollRef} className="ia-comms-thread-scroll flex-1 overflow-auto min-h-0 rounded-b-xl" data-thread-scroll="true">
                <PullToRefreshIndicator state={pullState} label="conversation list" />
                {!['campaigns', 'scheduled', 'voicemail'].includes(inboxTab) && (
                    <div className="flex justify-end px-2 py-1" data-thread-status-help="true">
                        <HoverHelp helpId="communications.thread-flags" userId={userId} />
                    </div>
                )}
                {inboxTab === 'voicemail' ? (
                    <section className="p-3" aria-label="Voicemail">
                        <div className="mb-3 flex gap-2" role="tablist" aria-label="Voicemail status">
                            {['new', 'heard'].map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    role="tab"
                                    aria-selected={voicemailTab === tab}
                                    onClick={() => setVoicemailTab(tab)}
                                    className={`ia-btn3d ia-comms-pill ${voicemailTab === tab ? 'ia-btn3d--go ia-comms-pill--on' : ''}`}
                                >
                                    {tab === 'new' ? 'New' : 'Heard'}
                                </button>
                            ))}
                        </div>
                        {voicemailStateError && (
                            <p role="alert" className="mb-3 rounded-lg border border-red-400/40 bg-red-950/30 px-3 py-2 text-sm text-red-200">
                                {voicemailStateError}
                            </p>
                        )}
                        {voicemailStateLoading ? (
                            <p className="py-6 text-center text-sm text-white">Loading voicemail…</p>
                        ) : visibleVoicemails.length === 0 ? (
                            <p className="py-6 text-center text-sm text-white">
                                {voicemailTab === 'new' ? 'No new voicemails.' : 'No heard voicemails.'}
                            </p>
                        ) : (
                            <div className="divide-y divide-[#26365A]">
                                {visibleVoicemails.map(({ thread, message, state }) => (
                                    <div key={`${thread.id}:${message.callSid}`} className="flex items-center gap-2 py-3">
                                        <button
                                            type="button"
                                            onClick={() => onSelect?.(thread.id)}
                                            className="min-w-0 flex-1 text-left"
                                        >
                                            <span className="block truncate text-sm font-semibold text-white">{threadDisplayName(thread)}</span>
                                            <span className="mt-1 block text-xs text-[#A9B7D0]">
                                                Voicemail · {message.callDuration ?? 0}s · {state?.heard_at ? 'Heard' : 'New'}
                                            </span>
                                        </button>
                                        <span className="shrink-0 text-xs text-[#A9B7D0]">{formatThreadActivityTimestamp(message.at)}</span>
                                        {!state?.heard_at && (
                                            <button
                                                type="button"
                                                onClick={() => onUpdateVoicemailState?.({
                                                    sessionId: thread.id,
                                                    callSid: message.callSid,
                                                    changes: { heard_at: new Date().toISOString() },
                                                })}
                                                className="ia-btn3d ia-btn3d--go shrink-0 px-2 py-1 text-xs"
                                            >
                                                Mark heard
                                            </button>
                                        )}
                                        <ActionPillMenu
                                            menuLabel="Voicemail actions"
                                            placement="left"
                                            mobilePlacement="left"
                                            align="end"
                                            menuClassName="ia-voicemail-actions"
                                            wrapperProps={{className:"ml-auto shrink-0",'data-voicemail-actions':true}}
                                            triggerClassName="ia-btn3d ia-comms-pill shrink-0 px-3 py-2 text-xs"
                                            options={[
                                                {
                                                    key: 'archive',
                                                    className:'ia-btn3d ia-comms-pill',
                                                    label: 'Archive',
                                                    onSelect: () => onUpdateVoicemailState?.({
                                                        sessionId: thread.id,
                                                        callSid: message.callSid,
                                                        changes: { archived_at: new Date().toISOString() },
                                                    }),
                                                },
                                                {
                                                    key: 'delete',
                                                    className:'ia-btn3d ia-comms-pill',
                                                    label: 'Delete voicemail',
                                                    onSelect: () => onUpdateVoicemailState?.({
                                                        sessionId: thread.id,
                                                        callSid: message.callSid,
                                                        changes: { deleted_at: new Date().toISOString() },
                                                    }),
                                                },
                                            ]}
                                        >
                                            Actions
                                        </ActionPillMenu>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                ) : inboxTab === 'scheduled' ? (
                    <ScheduledList
                        rows={scheduledRows}
                        threads={threads}
                        staffNames={staffNames}
                        userId={userId}
                        isAdmin={isAdmin}
                        error={scheduledError}
                        onOpen={onOpenScheduled}
                        onCancel={onCancelScheduled}
                        quietHours={quietHours}
                        onSaveQuietHours={onSaveQuietHours}
                    />
                ) : inboxTab === 'campaigns' ? (
                    campaignError ? (
                        <div className="p-6 text-center text-sm text-[#FCA5A5]">Could not load campaigns: {campaignError}</div>
                    ) : selectedCampaignId ? (() => {
                        const campaign = campaigns.find((item) => item.id === selectedCampaignId);
                        if (!campaign) return <div className={`p-6 text-sm ${labelCls}`}>Campaign not found.</div>;
                        return (
                            <div className="p-3">
                                <button type="button" onClick={() => setSelectedCampaignId('')} className="mb-3 text-xs text-[#5EEAD4] underline">← All campaigns</button>
                                <h3 className="text-sm font-semibold">{campaign.displayName}</h3>
                                <div className={`my-2 text-xs ${labelCls}`}>
                                    Delivered {campaign.counts.delivered} · Replied {campaign.counts.replied} · Opted out {campaign.counts.optedOut} · Skipped {campaign.counts.skipped}
                                </div>
                                <div className="divide-y">
                                    {campaign.recipients.map((recipient) => (
                                        <div key={recipient.id} className="flex items-center justify-between gap-2 py-2 text-xs">
                                            <div className="min-w-0">
                                                <div className="truncate font-medium">{recipient.contactName || recipient.destination}</div>
                                                <div className={labelCls}>{formatPhone(recipient.destination) || recipient.destination} · {recipient.displayStatus}</div>
                                            </div>
                                            {recipient.intake_session_id && (
                                                <button type="button" onClick={() => onOpenCampaignThread?.(recipient.intake_session_id)} className="shrink-0 text-[#5EEAD4] underline">Open thread</button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })() : campaigns.length === 0 ? (
                        <div className={`p-8 text-center text-sm ${labelCls}`}>No campaigns yet.</div>
                    ) : campaigns.map((campaign) => (
                        <button
                            key={campaign.id}
                            type="button"
                            onClick={() => setSelectedCampaignId(campaign.id)}
                            className={`ia-hover-pulse-off w-full border-b px-3 py-3 text-left ${dividerCls}`}
                        >
                            <span className="block truncate text-sm font-medium">{campaign.displayName}</span>
                            <span className={`mt-1 block text-xs ${labelCls}`}>
                                {campaign.channel} · {campaign.sentDate} · {campaign.recipientCount} recipients · delivered {campaign.counts.delivered} · replied {campaign.counts.replied} · opted out {campaign.counts.optedOut} · skipped {campaign.counts.skipped}
                            </span>
                        </button>
                    ))
                ) : (
                loading ? (
                    <div className={`p-8 text-center text-sm ${labelCls}`}>Loading conversations…</div>
                ) : error ? (
                    <div className="p-6 text-center text-sm text-[#FCA5A5]">Could not load conversations: {error}</div>
                ) : visible.length === 0 ? (
                    <div className={`p-8 text-center text-sm ${labelCls}`}>
                        {threads.length === 0
                            ? (inboxScope === 'mine' ? 'Nothing in your inbox yet. Take a conversation from the Company inbox.' : 'No conversations yet.')
                            : 'No conversations match that search.'}
                    </div>
                ) : groupThreadsByStatus ? (
                    groupedStatusThreads.map((group) => (
                        <section key={group.status} aria-label={`${group.label} threads`}>
                            <div
                                className={`ia-comms-group-header sticky top-0 z-20 border-b px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide ${dividerCls} bg-[#16223B] text-white`}
                                data-thread-group-header="status"
                            >
                                {group.label}
                            </div>
                            {arrangePinnedThreads(group.threads).map(renderThread)}
                        </section>
                    ))
                ) : groupThreadsByAccount ? (
                    groupedThreads.map((group) => (
                        <section key={group.key} aria-label={`${group.accountName} threads`}>
                            <div
                                className={`ia-comms-group-header sticky top-0 z-20 flex min-h-11 items-center justify-between gap-2 border-b px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide ${dividerCls} bg-[#16223B] text-white`}
                                data-thread-group-header={group.kind}
                            >
                                <span className="min-w-0 truncate">{group.accountName}</span>
                                {group.notInCrm && onAddToCrm && renderAddToCrm(group.threads)}
                            </div>
                            {arrangePinnedThreads(group.threads).map(renderThread)}
                        </section>
                    ))
                ) : (
                    arrangePinnedThreads(visible).map(renderThread)
                )
                )}
                {inboxTab === 'conversations' && colleagueSection}
            </div>
        </div>
    );
}

export default CommunicationsThreadList;
